import type { Asset, BalanceAccount } from "rujira.js";

export type TransferBalanceEntry =
  | { status: "ready"; account: BalanceAccount }
  | { status: "error" };

export type TransferBalanceState = {
  key: string;
  entries: Record<string, TransferBalanceEntry>;
};

/**
 * Folds a balance fetch result into the state. Pass `accounts` on success and
 * omit it on failure. A failed refresh keeps the last good balances for the
 * same request, so a transient error cannot replace a form that is being
 * filled in; an error only shows when there is nothing good to keep.
 */
export const nextTransferBalanceState = ({
  previous,
  key,
  addresses,
  asset,
  accounts,
}: {
  previous?: TransferBalanceState;
  key: string;
  addresses: readonly string[];
  asset: Asset;
  accounts?: readonly BalanceAccount[];
}): TransferBalanceState => {
  if (
    !accounts &&
    previous?.key === key &&
    Object.values(previous.entries).some((entry) => entry.status === "ready")
  )
    return previous;

  return {
    key,
    entries: Object.fromEntries(
      addresses.map((address): [string, TransferBalanceEntry] => {
        if (!accounts) return [address, { status: "error" }];
        const account = accounts.find(
          (item) => item.address === address && item.asset.asset === asset.asset
        );
        return [
          address,
          {
            status: "ready",
            account: account || { address, asset, balance: 0n, valueUsd: 0n },
          },
        ];
      })
    ),
  };
};
